import { useMutation } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import { DefaultResponseWithData } from "../models/system";
import apiConfig from "../utils/apiConfig";

export interface EbayToShopifyBackfillPart {
  partDataId: string;
  tagNumber: string | null;
  title: string | null;
  ebayItemNumber: string | null;
}

export interface EbayToShopifyBackfillItem {
  partDataId: string;
  tagNumber: string | null;
  ebayItemNumber: string | null;
  success: boolean;
  message: string | null;
  /** State behind a skip - a part sold yesterday reads differently from one sold in February. */
  soldDate: string | null;
  removedOn: string | null;
  isDamaged: boolean;
  listingStarted: string | null;
  listingEnded: string | null;
  /** Set when the part was skipped because a Shopify product row already exists. */
  existingShopifyProductId: string | null;
  existingShopifyPublished: boolean | null;
}

export interface EbayToShopifyBackfillResult {
  requested: number;
  succeeded: number;
  failed: number;
  dryRun: boolean;
  /** Tag numbers with no matching part. */
  unmatchedTagNumbers: string[];
  /** Tag numbers a real run would create - the cross-reference list. */
  eligibleTagNumbers: string[];
  items: EbayToShopifyBackfillItem[];
}

export interface BackfillAllRequest {
  /** Parts to process this run (1-500). */
  take: number;
  /** Parts created in parallel (1-8). */
  maxConcurrency?: number;
  /** Report what would be created without creating it. */
  dryRun: boolean;
}

export default function useShopifyEbayBackfill(componentName = "useShopifyEbayBackfill") {
  // A batch creates products and uploads their images to Shopify one part at a time, so a full run
  // outlasts any fixed client timeout - run it uncapped (timeoutMs: 0), as the media sweep does.
  const { get, post } = useApiHelper({ ...apiConfig(componentName), timeoutMs: 0 });

  const getPartsWithoutShopifyListing = useMutation(
    () => get<DefaultResponseWithData<EbayToShopifyBackfillPart[]>>("/shopify/ebay-backfill/parts")
  );

  // Server-side batch: scans the candidates itself and processes up to `take` parts across
  // concurrent workers - one request per batch instead of feeding it a list of ids.
  const backfillAll = useMutation(({ take, maxConcurrency = 4, dryRun }: BackfillAllRequest) =>
    post<Record<string, never>, DefaultResponseWithData<EbayToShopifyBackfillResult>>(
      `/shopify/ebay-backfill/all?take=${take}&maxConcurrency=${maxConcurrency}&dryRun=${dryRun}`,
      {}
    )
  );

  const backfillTagNumbers = useMutation(({ tagNumbers, dryRun }: { tagNumbers: string[]; dryRun: boolean }) =>
    post<{ tagNumbers: string[]; dryRun: boolean; maxConcurrency: number }, DefaultResponseWithData<EbayToShopifyBackfillResult>>(
      "/shopify/ebay-backfill",
      { tagNumbers, dryRun, maxConcurrency: 4 }
    )
  );

  return { getPartsWithoutShopifyListing, backfillAll, backfillTagNumbers };
}
